import type { MouseEvent, KeyboardEvent } from "react";
import type { User } from "@/services/api/user";
import { getDisplayName } from "@/lib/user";
import { humanizeEnumValue } from "@/lib/utils";
import { getMemberStatusTone } from "@/lib/statusTone";
import { formatDate } from "@/lib/dateFormat";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import RowActionsMenu from "@/components/ActionCells/RowActionsMenu";
import type { RowAction } from "@/components/ActionCells/RowActionsMenu";
import Eye from "@/components/SVGComponents/Eye";
import CrossIcon from "@/components/SVGComponents/CrossIcon";
import { formatMemberSubtitle, formatRoom } from "../lib/memberFormat";
import styles from "../Members.module.scss";

interface MemberCardProps {
  user: User;
  onCardClick: (user: User) => void;
  onBlockClick: (user: User) => void;
}

const getCardActions = (
  user: User,
  onViewClick: (user: User) => void,
  onBlockClick: (user: User) => void,
): RowAction[] => {
  const actions: RowAction[] = [
    {
      key: "view",
      label: "View record",
      icon: <Eye width={16} height={16} strokeWidth={1.75} />,
      onClick: () => onViewClick(user),
    },
  ];

  if (user.status !== "BLOCK") {
    actions.push({
      key: "block",
      label: "Block",
      icon: <CrossIcon withBg={false} stroke="currentColor" strokeWidth={1.5} width={14} height={14} />,
      tone: "danger",
      onClick: () => onBlockClick(user),
    });
  }

  return actions;
};

const MemberCard = ({ user, onCardClick, onBlockClick }: MemberCardProps) => {
  const displayName = getDisplayName(user);
  const initials = displayName
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "M";

  const handleCardClick = () => {
    onCardClick(user);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onCardClick(user);
    }
  };

  const handleActionsClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const formattedJoinedDate = user.createdAt ? formatDate(user.createdAt) : "—";
  const city = user.cityName || user.location || "—";
  const room = formatRoom(user.userCategory);

  return (
    <div
      className={styles.mobileCard}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${displayName}`}
    >
      <div className={styles.mobileCardHeader}>
        <div className={styles.mobileAvatar}>
          <span>{initials}</span>
        </div>

        <div className={styles.mobileMemberInfo}>
          <h3 className={styles.mobileMemberName}>{displayName}</h3>
          <p className={styles.mobileMemberSubtitle}>{formatMemberSubtitle(user)}</p>
        </div>

        <div className={styles.mobileCardActions} onClick={handleActionsClick}>
          <StatusBadge
            label={user.status ? humanizeEnumValue(user.status) : "—"}
            tone={getMemberStatusTone(user.status)}
            className={styles.mobileStatusBadge}
          />
          <RowActionsMenu
            actions={getCardActions(user, onCardClick, onBlockClick)}
            ariaLabel={`Actions for ${displayName}`}
            triggerClassName={styles.mobileRowActionTrigger}
          />
        </div>
      </div>

      <div className={styles.mobileCardDivider} />

      <div className={styles.mobileCardGrid}>
        <div className={styles.mobileCardField}>
          <span className={styles.mobileFieldLabel}>Email</span>
          <span className={styles.mobileFieldValue}>{user.email || "—"}</span>
        </div>

        <div className={styles.mobileCardField}>
          <span className={styles.mobileFieldLabel}>Room</span>
          <span className={styles.mobileFieldValue}>
            {room ? <StatusBadge label={room} tone="neutral" className={styles.roomBadge} /> : "—"}
          </span>
        </div>

        <div className={styles.mobileCardField}>
          <span className={styles.mobileFieldLabel}>City</span>
          <span className={styles.mobileFieldValue}>{city}</span>
        </div>

        <div className={styles.mobileCardField}>
          <span className={styles.mobileFieldLabel}>Joined</span>
          <span className={styles.mobileFieldValue}>{formattedJoinedDate}</span>
        </div>

        {user.mobileNo && (
          <div className={styles.mobileCardField}>
            <span className={styles.mobileFieldLabel}>Mobile</span>
            <span className={styles.mobileFieldValue}>{user.mobileNo}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberCard;
